import { useEffect } from 'react';
import { getLogger } from './datadog';

type MemoryLoggerCallback = (memInfo?: {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
}) => void;

class MemoryLogger {
  private running = false;
  private callbacks = new Set<MemoryLoggerCallback>();
  constructor(readonly interval: number) {}

  subscribe(callback: MemoryLoggerCallback) {
    this.callbacks.add(callback);
    this.run();
    return () => {
      this.callbacks.delete(callback);
    };
  }

  private run() {
    if (this.running) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const self = this;
    setTimeout(function memoryProfilerTick() {
      if (self.callbacks.size === 0) {
        return;
      }
      const memory = performance.memory;

      for (const callback of self.callbacks) {
        callback(memory);
      }

      setTimeout(memoryProfilerTick, self.interval);
    }, this.interval);
  }
}

/**
 * This is a singleton that:
 * - Polls for memory info every 60s if any callbacks are registered
 * - calls all registered callbacks with the result of performance.now
 */
const memoryProfiler = new MemoryLogger(60_000);

/**
 * Logs memory usage, randomly sampled by samplingRate
 */
export function useLogMemoryToDD(tag: string, samplingRate: number) {
  useEffect(() => {
    return memoryProfiler.subscribe(memInfo => {
      if (memInfo == null) {
        return;
      }

      const clampedSR = Math.max(0, Math.min(1, samplingRate));
      if (clampedSR !== samplingRate) {
        console.info(
          `useLogMemoryToDD: sampling rate ${samplingRate} doesn't fall between 0 and 1. Clamped to ${clampedSR}`,
        );
      }

      // Math.random is [0,1), so we want >= here
      if (Math.random() >= clampedSR) {
        return;
      }

      const info = {
        tag,
        browsingTime: performance.now(),
        memInfo: {
          jsHeapSizeLimit: memInfo.jsHeapSizeLimit,
          totalJSHeapSize: memInfo.totalJSHeapSize,
          usedJSHeapSize: memInfo.usedJSHeapSize,
        },
      };

      getLogger().info('memory', info);
    });
  }, [samplingRate, tag]);
}
